import { coachingPricing, CoachingTermLength } from './coachingPrograms'

export type PackageType = 'in_person' | 'online' | 'meal_plan'

export interface PackageTerm extends CoachingTermLength {
  label: string
  price?: string
  detail?: string
}

export interface CatalogPackage {
  name: string
  packageType: PackageType
  description: string
  priceLabel: string
  price: number
  sessionsIncluded?: number
  features: string[]
  terms: PackageTerm[]
}

const DAYS_PER_MONTH = 30

export function termToDays(length: CoachingTermLength): number {
  if (length.unit === 'day') return length.value
  if (length.unit === 'week') return length.value * 7
  return length.value * DAYS_PER_MONTH
}

/** End date for a term. Months follow the calendar so a 3-month package lands on the same day-of-month. */
export function addTerm(start: Date, length: CoachingTermLength): Date {
  const end = new Date(start)
  if (length.unit === 'month') {
    end.setMonth(end.getMonth() + length.value)
  } else {
    end.setDate(end.getDate() + termToDays(length))
  }
  return end
}

export function formatTermLength(length: CoachingTermLength): string {
  const plural = length.value === 1 ? '' : 's'
  return `${length.value} ${length.unit}${plural}`
}

const termsFrom = (key: keyof typeof coachingPricing): PackageTerm[] =>
  coachingPricing[key].map((option) => ({
    label: option.label,
    price: option.price,
    detail: option.detail,
    value: option.length?.value ?? 12,
    unit: option.length?.unit ?? 'week',
  }))

/**
 * Cassie's real programs, mirroring what the public service pages advertise.
 * Prices come from `coachingPrograms.ts` so the site and the admin panel never drift apart.
 */
export const DEFAULT_PACKAGES: CatalogPackage[] = [
  {
    name: 'The Strong Again Method — Private',
    packageType: 'in_person',
    description:
      'Signature strength and body-composition coaching for women over 40, built to create results that work in real life.',
    priceLabel: 'From $497/m',
    price: 497,
    features: [
      'Fully customized strength-training program',
      'Personalized nutrition coaching',
      'Regular progress reviews and coaching consultations',
      'Training and nutrition adjustments based on your results',
      'Recovery, stress-management, and real-life consistency strategies',
      'Select in-person coaching available for DMV-area clients',
    ],
    terms: termsFrom('strongAgain'),
  },
  {
    name: "Champion's Blueprint",
    packageType: 'in_person',
    description:
      'Competition prep coaching to give you the confidence and edge you need on show day.',
    priceLabel: 'From $1,500',
    price: 1500,
    features: [
      'Fully customized training, nutrition, cardio & supplementation',
      'Ongoing physique assessments to enhance strengths',
      'Weekly consultations and progress check-ins via email & phone',
      'Personalized posing and stage presentation coaching',
      'Pre-contest check-ins and peak-week adjustments',
      'Post-contest analysis and next-goal planning',
      'Complimentary pre-prep consultation',
    ],
    terms: termsFrom('championsBlueprint'),
  },
  {
    name: 'The Pro Look',
    packageType: 'in_person',
    description:
      'Achieve a lean, sculpted, competition-level physique — no stage required.',
    priceLabel: 'From $1,350',
    price: 1350,
    features: [
      'Fully customized training, nutrition, cardio & supplementation',
      'Physique assessments to highlight strengths & target weaknesses',
      'Weekly check-ins via email or phone with progress tracking',
      'Continuous plan adjustments based on results',
      'Individualized coaching and accountability',
      'Post-event transition strategy to maintain your look',
      'Complimentary consultation to determine your timeline',
    ],
    terms: termsFrom('proLook'),
  },
  {
    name: 'The Strong Again Method — Online',
    packageType: 'online',
    description:
      'Signature strength and body-composition coaching for women over 40 who are ready to stop starting over.',
    priceLabel: 'From $497/m',
    price: 497,
    features: [
      'Fully customized strength-training program',
      'Personalized nutrition coaching',
      'Regular progress reviews and coaching consultations',
      'Training and nutrition adjustments based on your results',
      'Recovery, stress-management, and real-life consistency strategies',
      'Available worldwide via virtual coaching',
    ],
    terms: termsFrom('strongAgain'),
  },
  {
    name: 'Elite Competitive Edge',
    packageType: 'online',
    description:
      'Turn the offseason into your most powerful growth phase after competition.',
    priceLabel: 'From $1,197',
    price: 1197,
    features: [
      'Immediate post-contest transition guidelines',
      'Custom nutrition, training, cardio & supplementation plans',
      'Bi-weekly one-on-one consultations',
      'Post-show cravings and rebound management tools',
      'Conditioning maintenance while building offseason physique',
      'Phone or in-person check-ins (DMV area available)',
    ],
    terms: termsFrom('eliteCompetitiveEdge'),
  },
  {
    name: 'Nutrition Blueprint',
    packageType: 'meal_plan',
    description:
      'Personalized nutrition coaching for clients who already have an established training routine but need more structure, clarity, and accountability with food.',
    priceLabel: '$597',
    price: 597,
    sessionsIncluded: 3,
    features: [
      'Initial nutrition and lifestyle assessment',
      'Personalized nutrition strategy',
      'Calorie and protein recommendations when appropriate',
      'Meal-building guidance',
      'Three private coaching consultations',
      'Scheduled progress reviews and nutrition adjustments',
      'Strategies for weekends, dining out, travel, and social events',
      'Final transition and maintenance plan',
    ],
    terms: termsFrom('nutritionBlueprint'),
  },
]

/** Offered when a package isn't one of Cassie's standard programs. */
export const GENERIC_TERMS: PackageTerm[] = [
  { label: '4 Weeks', value: 4, unit: 'week' },
  { label: '8 Weeks', value: 8, unit: 'week' },
  { label: '12 Weeks', value: 12, unit: 'week' },
  { label: '3 Months', value: 3, unit: 'month' },
  { label: '6 Months', value: 6, unit: 'month' },
  { label: '12 Months', value: 12, unit: 'month' },
]

const normalize = (name: string) => name.trim().toLowerCase()

export function findCatalogPackage(name: string): CatalogPackage | undefined {
  return DEFAULT_PACKAGES.find((pkg) => normalize(pkg.name) === normalize(name))
}

/**
 * Term choices for a package. Standard programs get the lengths advertised on the
 * site; anything Cassie created by hand gets its own configured length plus common ones.
 */
export function termsForPackage(pkg: {
  name: string
  duration_days?: number
}): PackageTerm[] {
  const catalogMatch = findCatalogPackage(pkg.name)
  if (catalogMatch) return catalogMatch.terms

  const configuredDays = pkg.duration_days
  if (!configuredDays) return GENERIC_TERMS

  const alreadyListed = GENERIC_TERMS.some(
    (term) => termToDays(term) === configuredDays
  )
  if (alreadyListed) return GENERIC_TERMS

  return [
    { label: `${configuredDays} Days (package default)`, value: configuredDays, unit: 'day' },
    ...GENERIC_TERMS,
  ]
}
